<template>
  <div class="hobbies-card">
    <h4 class="hobbies-card__title">{{ $t('page.about.hobbiesCard.title') }}</h4>
    <p class="hobbies-card__text">
      {{ $t('page.about.hobbiesCard.text') }}
    </p>
    <div class="hobbies-card__chips">
      <span v-for="hobby in visibleHobbies" :key="hobby" class="hobby-chip">
        {{ hobby }}
      </span>

      <!-- More Chip with Popover/Dropdown behavior on hover -->
      <div
        v-if="remainingHobbiesCount > 0"
        class="hobby-more-wrapper"
        @mouseenter="showPopover = true"
        @mouseleave="showPopover = false"
      >
        <span class="hobby-chip hobby-chip--more"> +{{ remainingHobbiesCount }} </span>

        <!-- Popover -->
        <div class="hobby-popover" :class="{ 'is-visible': showPopover }">
          <div class="hobby-popover__content">
            <span v-for="hobby in hiddenHobbies" :key="hobby" class="hobby-chip hobby-chip--hidden">
              {{ hobby }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { tm } = useI18n()
const hobbies = computed(() => tm('page.about.hobbiesCard.hobbies'))

const MAX_VISIBLE = 7
const showPopover = ref(false)

const visibleHobbies = computed(() => {
  return hobbies.value.slice(0, MAX_VISIBLE)
})

const hiddenHobbies = computed(() => {
  return hobbies.value.slice(MAX_VISIBLE)
})

const remainingHobbiesCount = computed(() => {
  return Math.max(0, hobbies.value.length - MAX_VISIBLE)
})
</script>

<style scoped>
.hobbies-card {
  @apply p-4 h-full flex flex-col;
}

.hobbies-card__title {
  @apply text-xs uppercase tracking-wider mb-2;
  color: var(--color-text-muted);
}

.hobbies-card__text {
  @apply text-sm leading-relaxed mb-3;
  color: var(--color-text-secondary);
}

.hobbies-card__chips {
  @apply flex flex-wrap gap-1.5 relative;
}

.hobby-chip {
  @apply px-2 py-1 rounded-full text-xs transition-all duration-300 cursor-default;
  background: var(--color-surface-elevated);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.hobby-more-wrapper {
  position: relative;
  display: inline-block;
}

.hobby-chip--more {
  @apply cursor-help font-bold;
  background: rgba(124, 58, 237, 0.1);
  color: var(--color-electric-purple);
}

.hobby-chip--more:hover {
  background: var(--color-electric-purple);
  color: white;
  transform: scale(1.05);
}

/* Popover Styles */
.hobby-popover {
  position: absolute;
  bottom: 100%;
  right: 0; /* Anchor to right */
  left: auto; /* Remove left anchor */
  transform: translateY(10px) scale(0.95);
  margin-bottom: 8px;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 8px;
  width: max-content;
  max-width: 200px;
  z-index: 50;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
  pointer-events: none;
}

.hobby-popover.is-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}

/* Arrow indicator for popover */
.hobby-popover::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 12px; /* Position relative to the right edge */
  left: auto;
  transform: none;
  border-width: 6px;
  border-style: solid;
  border-color: var(--color-surface-elevated) transparent transparent transparent;
}

.hobby-popover::before {
  content: '';
  position: absolute;
  top: 100%;
  right: 11px; /* Position relative to the right edge + border width offset */
  left: auto;
  transform: none;
  border-width: 7px;
  border-style: solid;
  border-color: var(--color-border) transparent transparent transparent;
  margin-top: 1px;
  z-index: -1;
}

.hobby-popover__content {
  @apply flex flex-wrap gap-1.5 justify-center;
}

.hobby-chip--hidden {
  @apply bg-opacity-80;
}
</style>
